import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import type { WarehouseLocationTemplateDTO } from "../../../client"

export type LocationPickerModalProps = {
  isOpen: boolean
  onClose: () => void
  onChoose?: (code: string) => void
  onChooseMultiple?: (codes: string[]) => void
  isMultiSelect?: boolean
  title?: string
  maxResults?: number // default 200
  template?: WarehouseLocationTemplateDTO | null
  suggestedLocations?: SuggestedLocation[]
}

export type SuggestedLocation = {
  code: string
  source: "stock" | "history"
  qty?: number
  lastSeenAt?: string
}

// Helpers to format with leading zeros
const MAX_VISITS = 120000
const LARGE_TEMPLATE_THRESHOLD = 80000

const matchesLocationSearch = (
  code: string,
  query: string,
  separator: string,
) => {
  const norm = query.trim().toUpperCase()
  if (!norm) return true
  const upper = code.toUpperCase()
  if (upper.startsWith(norm) || upper.includes(norm)) return true
  const parts = upper.split(separator).filter(Boolean)
  return parts.some((p) => p.startsWith(norm) || p.includes(norm))
}

export function generateLocationsFromTemplate(
  prefix: string,
  maxResults: number,
  template: WarehouseLocationTemplateDTO,
): string[] {
  if (template.segments && template.segments.length > 0) {
    return generateBySegments(
      prefix,
      maxResults,
      template.separator || "-",
      template.segments,
    )
  }
  const results: string[] = []
  const norm = prefix.trim().toUpperCase()
  const sep = template.separator || "-"
  const zoneFrom = (template.zone_from || "A").toUpperCase().charAt(0)
  const zoneTo = (template.zone_to || "Z").toUpperCase().charAt(0)
  const rackPad = Math.max(1, template.rack_pad || 3)
  const modulePad = Math.max(1, template.module_pad || 3)
  const aislePad = Math.max(1, template.aisle_pad || 3)
  const levelPad = Math.max(1, template.level_pad || 2)
  const rackPrefix = (template.rack_prefix || "R").toUpperCase()
  const modulePrefix = (template.module_prefix || "M").toUpperCase()
  const aislePrefix = (template.aisle_prefix || "P").toUpperCase()
  const levelPrefix = (template.level_prefix || "L").toUpperCase()
  const wholeLabel = (template.whole_label || "CAŁY").toUpperCase()
  const pad = (n: number, width: number) => n.toString().padStart(width, "0")
  const estimate =
    Math.max(1, zoneTo.charCodeAt(0) - zoneFrom.charCodeAt(0) + 1) *
    Math.max(
      1,
      template.include_aisle
        ? (template.aisle_to ?? 1) - (template.aisle_from ?? 1) + 1
        : 1,
    ) *
    Math.max(1, (template.rack_to ?? 1) - (template.rack_from ?? 1) + 1) *
    Math.max(
      1,
      template.include_level
        ? (template.level_to ?? 1) - (template.level_from ?? 1) + 1
        : 1,
    ) *
    Math.max(1, (template.module_to ?? 1) - (template.module_from ?? 1) + 1)
  if (estimate > LARGE_TEMPLATE_THRESHOLD && norm.length < 2) {
    return []
  }
  let visits = 0

  for (
    let code = zoneFrom.charCodeAt(0);
    code <= zoneTo.charCodeAt(0) && results.length < maxResults;
    code++
  ) {
    if (visits++ > MAX_VISITS) break
    const zone = String.fromCharCode(code)
    if (template.include_whole) {
      const whole = `${zone}${sep}${wholeLabel}`
      if (matchesLocationSearch(whole, norm, sep)) results.push(whole)
      if (results.length >= maxResults) break
    }
    const aisleFrom = template.include_aisle ? (template.aisle_from ?? 1) : 1
    const aisleTo = template.include_aisle ? (template.aisle_to ?? 1) : 1
    const levelFrom = template.include_level ? (template.level_from ?? 1) : 1
    const levelTo = template.include_level ? (template.level_to ?? 1) : 1

    for (let a = aisleFrom; a <= aisleTo && results.length < maxResults; a++) {
      if (visits++ > MAX_VISITS) break
      const partsBase = [zone]
      if (template.include_aisle) {
        partsBase.push(`${aislePrefix}${pad(a, aislePad)}`)
      }
      for (
        let r = template.rack_from;
        r <= template.rack_to && results.length < maxResults;
        r++
      ) {
        if (visits++ > MAX_VISITS) break
        const partsRack = [...partsBase, `${rackPrefix}${pad(r, rackPad)}`]
        for (
          let l = levelFrom;
          l <= levelTo && results.length < maxResults;
          l++
        ) {
          if (visits++ > MAX_VISITS) break
          const partsLevel = [...partsRack]
          if (template.include_level) {
            partsLevel.push(`${levelPrefix}${pad(l, levelPad)}`)
          }
          for (
            let m = template.module_from;
            m <= template.module_to && results.length < maxResults;
            m++
          ) {
            if (visits++ > MAX_VISITS) break
            const mStr = `${modulePrefix}${pad(m, modulePad)}`
            const codeStr = [...partsLevel, mStr].join(sep)
            if (matchesLocationSearch(codeStr, norm, sep)) results.push(codeStr)
          }
        }
      }
    }
  }
  return results
}

function generateBySegments(
  prefix: string,
  maxResults: number,
  sep: string,
  segments: any[], // use any[] to avoid strict type errors for start_value/end_value if not typed properly
): string[] {
  const out: string[] = []
  const norm = prefix.trim().toUpperCase()
  if (!segments || segments.length === 0) return out
  const estimate = segments
    .filter((s) => s.enabled && !s.is_static)
    .reduce((acc, s) => {
      if (s.mode === "alpha") {
        const from = (s.start_value || "A").charCodeAt(0)
        const to = (s.end_value || "A").charCodeAt(0)
        return acc * Math.max(1, to - from + 1)
      }
      const from = typeof s.from === "number" ? s.from : 1
      const to = typeof s.to === "number" ? s.to : from
      return acc * Math.max(1, to - from + 1)
    }, 1)
  if (estimate > LARGE_TEMPLATE_THRESHOLD && norm.length < 2) {
    return []
  }
  let visits = 0

  const build = (idx: number, parts: string[]) => {
    if (visits++ > MAX_VISITS) return
    if (out.length >= maxResults) return
    if (idx >= segments.length) {
      const code = parts.join(sep)
      if (matchesLocationSearch(code, norm, sep)) out.push(code)
      return
    }
    const seg = segments[idx]
    if (!seg.enabled) {
      build(idx + 1, parts)
      return
    }
    if (seg.is_static) {
      build(idx + 1, [...parts, (seg.static || "").toUpperCase()])
      return
    }

    let loopFrom = 1
    let loopTo = 1
    let pad = 0
    let isAlpha = false

    if (seg.mode === "alpha") {
      isAlpha = true
      loopFrom = (seg.start_value || "A").toUpperCase().charCodeAt(0)
      loopTo = (seg.end_value || "A").toUpperCase().charCodeAt(0)
    } else {
      loopFrom = typeof seg.from === "number" ? seg.from : 1
      loopTo = typeof seg.to === "number" ? seg.to : loopFrom
      pad = Math.max(0, seg.pad || 0)
    }

    const pref = (seg.prefix || "").toUpperCase()

    // Fast path for top-level segment
    if (parts.length === 0) {
      const base = pref || ""
      if (norm.startsWith(base)) {
        if (!isAlpha) {
          const digits = norm.slice(base.length).match(/^(\d+)/)
          if (digits?.[1]) {
            const target = Number.parseInt(digits[1], 10)
            if (
              Number.isFinite(target) &&
              target >= loopFrom &&
              target <= loopTo
            ) {
              loopFrom = target
              loopTo = target
            }
          }
        } else {
          const char = norm.slice(base.length).charAt(0).toUpperCase()
          if (char) {
            const target = char.charCodeAt(0)
            if (target >= loopFrom && target <= loopTo) {
              loopFrom = target
              loopTo = target
            }
          }
        }
      }
    }

    for (let n = loopFrom; n <= loopTo && out.length < maxResults; n++) {
      let num = ""
      if (isAlpha) {
        num = String.fromCharCode(n)
      } else {
        num = pad > 0 ? n.toString().padStart(pad, "0") : n.toString()
      }
      build(idx + 1, [...parts, `${pref}${num}`])
    }
  }
  build(0, [])
  return out
}

export default function LocationPickerModal({
  isOpen,
  onClose,
  onChoose,
  onChooseMultiple,
  isMultiSelect = false,
  title = "Wybierz miejsce",
  maxResults = 200,
  template = null,
  suggestedLocations = [],
}: LocationPickerModalProps) {
  const [query, setQuery] = useState("")
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const effectiveTemplate = template ?? null
  const suggestedHeaderBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const suggestedHeaderColor = useColorModeValue("gray.700", "gray.100")
  const listBorderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const qtyColor = useColorModeValue("gray.500", "gray.400")
  const emptyStateColor = useColorModeValue("gray.500", "gray.400")

  const items = useMemo(
    () =>
      effectiveTemplate
        ? generateLocationsFromTemplate(query, maxResults, effectiveTemplate)
        : [],
    [query, maxResults, effectiveTemplate],
  )
  const visibleSuggestedLocations = useMemo(() => {
    const sep = effectiveTemplate?.separator || "-"
    return suggestedLocations.filter((item) =>
      matchesLocationSearch(item.code, query, sep),
    )
  }, [effectiveTemplate?.separator, query, suggestedLocations])

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    )
  }

  const handleSelectCode = (code: string) => {
    if (isMultiSelect) {
      toggleCode(code)
    } else {
      onChoose?.(code)
      onClose()
    }
  }

  const handleConfirmMultiSelect = () => {
    if (selectedCodes.length > 0) {
      if (onChooseMultiple) {
        onChooseMultiple(selectedCodes)
      } else if (onChoose) {
        for (const code of selectedCodes) {
          onChoose(code)
        }
      }
    }
    setSelectedCodes([])
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setSelectedCodes([])
        onClose()
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Input
              placeholder="Szukaj (np. A-, A-CAŁY, A-R001, A-R001-M0)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {visibleSuggestedLocations.length > 0 ? (
              <Box
                borderWidth="1px"
                borderColor={listBorderColor}
                borderRadius="md"
                overflow="hidden"
              >
                <Box px={3} py={2} bg={suggestedHeaderBg}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={suggestedHeaderColor}
                  >
                    Sugerowane lokalizacje
                  </Text>
                </Box>
                <List>
                  {visibleSuggestedLocations.map((item) => {
                    const isChecked = selectedCodes.includes(item.code)
                    return (
                      <ListItem
                        key={`${item.source}-${item.code}`}
                        borderTopWidth="1px"
                        borderColor={listBorderColor}
                      >
                        <Button
                          variant="ghost"
                          w="100%"
                          justifyContent="space-between"
                          borderRadius={0}
                          _hover={{ bg: rowHoverBg }}
                          onClick={() => handleSelectCode(item.code)}
                        >
                          <HStack spacing={3} minW={0}>
                            {isMultiSelect && (
                              <Checkbox
                                isChecked={isChecked}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  toggleCode(item.code)
                                }}
                              />
                            )}
                            <Text fontWeight="semibold">{item.code}</Text>
                            <Badge
                              colorScheme={
                                item.source === "stock" ? "green" : "blue"
                              }
                            >
                              {item.source === "stock"
                                ? "Na stanie"
                                : "Historia"}
                            </Badge>
                          </HStack>
                          {typeof item.qty === "number" ? (
                            <Text fontSize="xs" color={qtyColor}>
                              {item.qty.toFixed(2)}
                            </Text>
                          ) : null}
                        </Button>
                      </ListItem>
                    )
                  })}
                </List>
              </Box>
            ) : null}
            {!effectiveTemplate ? (
              <Text color="orange.500">
                Brak szablonu lokalizacji. Utwórz szablon i przypisz go do
                magazynu.
              </Text>
            ) : items.length === 0 ? (
              <Text color={emptyStateColor}>
                Brak wyników lub zbyt szeroki zakres. Wpisz min. 2 znaki, aby
                zawęzić.
              </Text>
            ) : (
              <List
                maxH="60vh"
                overflowY="auto"
                borderWidth="1px"
                borderColor={listBorderColor}
                borderRadius="md"
              >
                {items.map((code) => {
                  const isChecked = selectedCodes.includes(code)
                  return (
                    <ListItem
                      key={code}
                      borderBottomWidth="1px"
                      borderColor={listBorderColor}
                      _last={{ borderBottomWidth: 0 }}
                    >
                      <Button
                        variant="ghost"
                        w="100%"
                        justifyContent="flex-start"
                        _hover={{ bg: rowHoverBg }}
                        onClick={() => handleSelectCode(code)}
                      >
                        <HStack spacing={3}>
                          {isMultiSelect && (
                            <Checkbox
                              isChecked={isChecked}
                              onChange={(e) => {
                                e.stopPropagation()
                                toggleCode(code)
                              }}
                            />
                          )}
                          <Text>{code}</Text>
                        </HStack>
                      </Button>
                    </ListItem>
                  )
                })}
              </List>
            )}
            <Text fontSize="xs" color={emptyStateColor}>
              Pokazano maksymalnie {maxResults} wyników. Wpisz więcej znaków,
              aby zawęzić listę.
            </Text>
          </VStack>
        </ModalBody>
        {isMultiSelect && (
          <ModalFooter borderTopWidth="1px" borderColor={listBorderColor}>
            <Flex justify="space-between" align="center" w="full">
              <Text fontSize="sm" color="gray.500">
                Zaznaczone: {selectedCodes.length}
              </Text>
              <HStack spacing={2}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedCodes([])
                    onClose()
                  }}
                >
                  Anuluj
                </Button>
                <Button
                  colorScheme="blue"
                  size="sm"
                  isDisabled={selectedCodes.length === 0}
                  onClick={handleConfirmMultiSelect}
                >
                  Dodaj zaznaczone ({selectedCodes.length})
                </Button>
              </HStack>
            </Flex>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
